// Programmatic SEO — one source of truth for every Chrome-Dino keyword
// landing route. Each entry drives the <head> tags, the on-page H1/H2/body,
// and the sitemap.xml urlset. Adding a new keyword is a single-object edit:
// add here, list the slug in KEYWORD_SLUGS, and it flows through App.js
// router + sitemap generator automatically.
//
// Route pattern: https://daredino.com/<slug>
// All routes render the exact same Landing component (game, leaderboard,
// how-to-play, FAQ, footer) — only the head metadata + hero heading change.

export const CANONICAL_ORIGIN = "https://daredino.com";

const OG_IMAGE = "/og-image.svg";

/**
 * Each SEO entry:
 *   slug         : URL path segment (also used as canonical suffix)
 *   title        : <title> — under 60 chars ideally
 *   description  : <meta name="description"> — under 155 chars
 *   h1           : Hero H1 override (JSX-safe plain text)
 *   h1Accent     : Word(s) inside the H1 that get the yellow accent color
 *   h2           : Sub-heading under the H1 (uppercase, cyan)
 *   body         : Intro paragraph. Plain text — no JSX inside.
 *   eyebrow      : Tiny label above the H1
 *   keywords     : Comma-separated meta keywords (Google ignores but Bing/Yandex still use it)
 */
export const SEO_KEYWORDS = {
  "dino": {
    slug: "dino",
    title: "Dino — Play the Chrome T-Rex Runner Online Free | DareDino",
    description:
      "Dino — the classic Chrome T-Rex dinosaur runner online. Jump the cactus, dodge the pterodactyls, climb the live global leaderboard. Free, unblocked, mobile.",
    h1: "Dino — The Chrome T-Rex Runner, Reborn",
    h1Accent: "Dino",
    h2: "Chrome T-Rex Runner · Live Leaderboard · Free to Play",
    body:
      "Dino is the world's most-played browser mini-game — Chrome's little pixel T-Rex that appears when your internet drops. DareDino brings the full dino experience online: same one-button jump feel, cleaner physics, day/night rain phases, and a real global leaderboard that refreshes every three seconds.",
    eyebrow: "[ Dino // Chrome T-Rex Runner ]",
    keywords:
      "dino, dino game, dino runner, dino chrome, dinosaur, chrome dino, t-rex dino, dino online, dino jump, dino run",
  },
  "chrome-dino": {
    slug: "chrome-dino",
    title: "Chrome Dino Game — Play Google's T-Rex Runner Online | DareDino",
    description:
      "Play the original Chrome Dino game online — jump the cactus, dodge the pterodactyls, climb the live global leaderboard. Free, unblocked, no download.",
    h1: "Chrome Dino Game — Play the Google T-Rex Runner Online",
    h1Accent: "Chrome Dino",
    h2: "Free · Unblocked · Live Global Leaderboard",
    body:
      "DareDino is the modern re-build of the Chrome Dino Game everyone knows from the offline Chrome browser page. Same jump-and-dodge feel, 60fps canvas engine, real global rankings, and a Google login that saves your best runs across every device. Free to play, no download required.",
    eyebrow: "[ Chrome Dino Game // Online ]",
    keywords:
      "chrome dino, chrome dino game, google chrome dinosaur game, chrome offline game, t-rex chrome runner",
  },
  "dino-game": {
    slug: "dino-game",
    title: "Dino Game Online — Play the Endless Runner Free | DareDino",
    description:
      "Play the Dino Game online — a fast, skill-based endless runner with live global leaderboards and daily rewards. No download, works on mobile.",
    h1: "Dino Game Online — The Endless Runner That Never Ends",
    h1Accent: "Dino Game",
    h2: "Endless Runner · Real Skill · Live Rankings",
    body:
      "The Dino Game reborn: cleaner physics, smarter obstacle spacing, a tiered speed curve that keeps every run feeling fresh, and a leaderboard that refreshes every three seconds. Sign in with Google to save your top score forever.",
    eyebrow: "[ Dino Game // Endless Runner ]",
    keywords:
      "dino game, dino game online, dino runner, endless dino, dino game free",
  },
  "dinosaur-game": {
    slug: "dinosaur-game",
    title: "Dinosaur Game — Play the Chrome T-Rex Online Free | DareDino",
    description:
      "The Dinosaur Game — the classic Chrome T-Rex Runner rebuilt with global leaderboards, live rankings, and daily skill challenges. Play free online.",
    h1: "Dinosaur Game — The Classic T-Rex Runner, Reborn",
    h1Accent: "Dinosaur Game",
    h2: "Classic Chrome T-Rex · Live Leaderboard · Free Forever",
    body:
      "This is the Dinosaur Game you remember from the Chrome no-internet screen, upgraded with a competitive live leaderboard, daily rewards, and pixel-perfect canvas rendering. Jump the cacti, avoid the pterodactyls, climb the ranks.",
    eyebrow: "[ Dinosaur Game // T-Rex Runner ]",
    keywords:
      "dinosaur game, chrome dinosaur game, t-rex game, dinosaur runner, chrome offline dinosaur",
  },
  "google-dino": {
    slug: "google-dino",
    title: "Google Dino Game — Play the Chrome T-Rex Online | DareDino",
    description:
      "Play Google's Dino game online with a live global leaderboard. The Chrome T-Rex Runner remastered — free, unblocked, and works on any device.",
    h1: "Google Dino Game — The T-Rex Runner With a Global Leaderboard",
    h1Accent: "Google Dino",
    h2: "The Chrome T-Rex, Remastered · Live Global Rankings",
    body:
      "DareDino is the fastest way to play the Google Dino game outside your browser's offline screen. Same jump mechanics, faster physics, cleaner obstacles, and a real global ranking that updates in real time. Free forever.",
    eyebrow: "[ Google Dino // Chrome T-Rex ]",
    keywords:
      "google dino, google dino game, google chrome dino, google t-rex, google dinosaur",
  },
  "t-rex-game": {
    slug: "t-rex-game",
    title: "T-Rex Game — Play the Chrome Runner Online Free | DareDino",
    description:
      "Play the T-Rex Game online — the Chrome T-Rex Runner rebuilt with live global rankings and daily skill rewards. Free, unblocked, mobile friendly.",
    h1: "T-Rex Game — The Chrome Runner With Real Stakes",
    h1Accent: "T-Rex Game",
    h2: "Chrome T-Rex Runner · Live Leaderboard · Free to Play",
    body:
      "The T-Rex Game is the endless runner every browser player knows — DareDino keeps the muscle memory identical and layers on competitive rankings, daily rewards, and cross-device high scores. Jump, dodge, dominate.",
    eyebrow: "[ T-Rex Game // Chrome Runner ]",
    keywords:
      "t-rex game, t rex runner, chrome t-rex, t-rex chrome, t-rex dinosaur game",
  },
  unblocked: {
    slug: "unblocked",
    title: "Chrome Dino Unblocked — Play at School or Work Free | DareDino",
    description:
      "Play the Chrome Dino Game unblocked — works on any network, no download, no install. Skill-based endless runner with a live global leaderboard.",
    h1: "Chrome Dino Unblocked — Play Anywhere, No Install",
    h1Accent: "Unblocked",
    h2: "Works at School · Works at Work · Instant Play",
    body:
      "A fully unblocked Chrome Dino experience — no downloads, no plugins, no restricted-network hurdles. Just open the page and jump. Live leaderboards keep every break-time run feeling meaningful.",
    eyebrow: "[ Dino Game // Unblocked ]",
    keywords:
      "chrome dino unblocked, dino game unblocked, unblocked dinosaur game, dino runner unblocked, no-download dino",
  },
  offline: {
    slug: "offline",
    title: "Chrome Dino Offline — Play the T-Rex Runner Anywhere | DareDino",
    description:
      "The offline Chrome Dino Game — play the T-Rex Runner without waiting for a lost connection. Live rankings sync when you're back online.",
    h1: "Chrome Dino Offline — The T-Rex Runner, Always Available",
    h1Accent: "Offline",
    h2: "No Wi-Fi? No Problem · Play Anytime",
    body:
      "Modeled after the beloved Chrome offline dinosaur screen, DareDino runs on any browser and any device. When your connection returns, your scores sync straight to the live global leaderboard.",
    eyebrow: "[ Chrome Dino // Offline ]",
    keywords:
      "chrome dino offline, offline dinosaur game, offline t-rex, offline chrome game, no internet dinosaur",
  },
  "no-internet-game": {
    slug: "no-internet-game",
    title: "No Internet Game — Play Chrome's Dino When Wi-Fi Drops | DareDino",
    description:
      "The famous 'No Internet' game from Chrome — the T-Rex Dino Runner — playable anywhere, with a live global leaderboard and daily rewards.",
    h1: "No Internet Game — Chrome's Dino Runner, Anywhere",
    h1Accent: "No Internet",
    h2: "The Wi-Fi-Down Classic · Now With a Global Board",
    body:
      "You know the one: your Wi-Fi drops and a little pixel dinosaur appears asking you to press space. That No-Internet Game is what we rebuilt — smoother, harder, competitive, and never gated behind an actual outage.",
    eyebrow: "[ No Internet Game // Dino Runner ]",
    keywords:
      "no internet game, no wifi dinosaur, chrome no internet dino, disconnected dinosaur game, wi-fi down game",
  },
  "dino-run": {
    slug: "dino-run",
    title: "Dino Run — Play the Endless Chrome Runner Online | DareDino",
    description:
      "Dino Run — the classic Chrome endless runner reborn with live global leaderboards, tiered difficulty, and daily skill rewards. Free to play.",
    h1: "Dino Run — Endless. Tiered. Ranked.",
    h1Accent: "Dino Run",
    h2: "Endless Runner · Tiered Difficulty · Live Rankings",
    body:
      "Dino Run pushes the classic Chrome endless-runner formula further with tiered difficulty ramps, precision hitboxes, and a competitive live leaderboard. Every run is a rank-climb attempt.",
    eyebrow: "[ Dino Run // Endless Runner ]",
    keywords:
      "dino run, dinorun, dino runner game, endless dino run, chrome dino run",
  },
  "dino-jump": {
    slug: "dino-jump",
    title: "Dino Jump — Time Your Jumps in the Chrome Runner | DareDino",
    description:
      "Dino Jump — master the timing in the fastest Chrome T-Rex Runner online. Live leaderboards, tiered speed ramps, and daily skill rewards.",
    h1: "Dino Jump — Where Timing Beats Luck",
    h1Accent: "Dino Jump",
    h2: "Precision Timing · Tiered Difficulty · Live Board",
    body:
      "Every jump in Dino Jump is a millisecond decision. Speed climbs across nine difficulty tiers, hitboxes are pixel-tight, and the leaderboard updates every three seconds — so every clean jump matters.",
    eyebrow: "[ Dino Jump // Skill Timing ]",
    keywords:
      "dino jump, jumping dino game, dino jump chrome, dino jumping runner, t-rex jump game",
  },
  "dark-mode": {
    slug: "dark-mode",
    title: "Chrome Dino Dark Mode — Play the T-Rex Runner at Night | DareDino",
    description:
      "The Chrome Dino Game in permanent dark mode — easier on the eyes, live global rankings, and daily skill rewards. Play free, unblocked, anywhere.",
    h1: "Chrome Dino Dark Mode — Endless Runner, Eyes Protected",
    h1Accent: "Dark Mode",
    h2: "Permanent Night Theme · Reduced Eye Strain · Live Rankings",
    body:
      "DareDino runs in a native dark-mode arcade palette — cyan grid, magenta dino, softened ground lines — so late-night runs never fry your eyes. Same competitive live leaderboard, same instant retry.",
    eyebrow: "[ Chrome Dino // Dark Mode ]",
    keywords:
      "chrome dino dark mode, dark dino game, night mode dinosaur, dark chrome dino, dino dark theme",
  },
};

// Ordered list — controls sitemap output order and any UI enumeration.
export const KEYWORD_SLUGS = [
  "dino",
  "chrome-dino",
  "dino-game",
  "dinosaur-game",
  "google-dino",
  "t-rex-game",
  "unblocked",
  "offline",
  "no-internet-game",
  "dino-run",
  "dino-jump",
  "dark-mode",
];

export function getSeoConfig(slug) {
  if (!slug) return null;
  return SEO_KEYWORDS[slug] || null;
}

/**
 * Apply a keyword's SEO metadata to the current <head>: title, description,
 * canonical, keywords, and OG tags. Idempotent and self-cleaning — replaces
 * existing tags marked with data-seo="1" so navigating between keyword
 * routes doesn't pile up stale metadata.
 */
export function applySeoHead(config) {
  if (typeof document === "undefined") return;
  if (!config) return;

  document.title = config.title;

  const setMeta = (attr, key, content) => {
    if (!content) return;
    let el = document.querySelector(`meta[${attr}="${key}"][data-seo="1"]`);
    if (!el) {
      // If the same-key tag exists without the data-seo marker (e.g. static
      // one from index.html), reuse it instead of duplicating.
      el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
    }
    el.setAttribute("content", content);
    el.setAttribute("data-seo", "1");
  };

  setMeta("name", "description", config.description);
  setMeta("name", "keywords", config.keywords);
  setMeta("property", "og:title", config.title);
  setMeta("property", "og:description", config.description);
  setMeta("property", "og:image", CANONICAL_ORIGIN + OG_IMAGE);
  setMeta("property", "og:url", CANONICAL_ORIGIN + "/" + config.slug);
  setMeta("property", "og:type", "website");
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", config.title);
  setMeta("name", "twitter:description", config.description);

  // Canonical — one and only one
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", CANONICAL_ORIGIN + "/" + config.slug);
}
